/** Compact External access grid for one partner, one API key or one tenant — the activity
 *  section of the partner page and of the API-credential drawer.
 *
 *  Same columns, chips and cell popovers as /external-access, minus what the scope already
 *  fixes. The scope goes to the backend as its authoritative param (`partner_id` /
 *  `credential_id` / `tour_operator_id`); the mandatory `created` window defaults to the
 *  last 7 days and is changed with the console time-window widget (clearing it returns to
 *  the default — the log cannot be read unbounded). Server-side pages of 25, newest first.
 *
 *  A row opens the call's detail drawer: here, or through `onOpenRow` when the host is
 *  itself a drawer (an overlay opened from inside another one is hidden from assistive tech
 *  — the host renders the detail drawer as its sibling instead). Cell quick filters and
 *  "Open in grid" open the full grid in a NEW tab with the scope and window applied.
 */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { DataGridPro, type GridPaginationModel } from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import * as externalAccessApi from '@/api/externalAccess';
import type { ExternalAccessItem } from '@/api/externalAccess';
import { equalityOperator, toWireAst, type FilterNode } from '@/components/QuerySearch';
import { boundToNaive, NOW, TimeWindowPicker, type TimeBound } from '@/components/TimeWindowPicker';
import { externalAccessHref, rangeCond } from '@/utils/gridLinks';
import { openInNewTab } from '@/utils/openInNewTab';

import { ExternalAccessDetailDrawer } from './ExternalAccessDetailDrawer';
import { buildColumns } from './externalAccessColumns';
import { buildExternalAccessNamespaces } from './externalAccessNamespaces';
import { resolveScope, type ExternalAccessScopeProps, type ScopeKind } from './externalAccessScope';

const PAGE_SIZE = 25;
export const EMBEDDED_WINDOW_DAYS = 7;

/** The columns worth their width once the scope is fixed. */
const FIELDS: Record<ScopeKind, string[]> = {
  partner: ['open', 'created_ts', 'channel', 'outcome', 'status', 'operation', 'principal', 'tenant', 'duration_ms', 'error_code'],
  // The credential drawer is 520 px wide: the call and how it ended.
  credential: ['open', 'created_ts', 'outcome', 'status', 'operation', 'error_code'],
  tenant: ['open', 'created_ts', 'channel', 'outcome', 'status', 'operation', 'principal', 'partner', 'duration_ms', 'error_code'],
};

type Range = [TimeBound, TimeBound];

/** The last EMBEDDED_WINDOW_DAYS days, live: it moves with every refresh, and the "open
 *  in grid" link carries it as `created=now-Nd..now`. */
function defaultRange(): Range {
  return [{ offset: -EMBEDDED_WINDOW_DAYS, unit: 'days' }, NOW];
}

export type EmbeddedExternalAccessProps = ExternalAccessScopeProps & {
  /** Open a row's details in a drawer the host renders (when the host is a drawer itself). */
  onOpenRow?: (row: ExternalAccessItem) => void;
};

export function EmbeddedExternalAccess(props: EmbeddedExternalAccessProps) {
  const { onOpenRow } = props;
  const scope = resolveScope(props);
  const namespaces = useMemo(() => buildExternalAccessNamespaces(), []);
  const [range, setRange] = useState<Range>(defaultRange);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: PAGE_SIZE });
  const [openRow, setOpenRow] = useState<ExternalAccessItem | null>(null);

  const windowNode: FilterNode = useMemo(
    () => rangeCond('created', boundToNaive(range[0]), boundToNaive(range[1])),
    [range],
  );

  const query = useQuery({
    queryKey: ['external-access', 'embedded', scope.kind, scope.id, windowNode, pagination.page, pagination.pageSize],
    queryFn: () =>
      externalAccessApi.list({
        ...scope.params,
        filters: toWireAst([windowNode]),
        ordering: '-created_ts',
        page: pagination.page + 1,
        page_size: pagination.pageSize,
      }),
    placeholderData: keepPreviousData,
  });

  // The full grid, in a new tab, with this scope and window (plus a quick-filter chip).
  const gridHref = useCallback(
    (extra: FilterNode[] = [], tenant: number | null = scope.tenant) =>
      externalAccessHref([...scope.nodes, ...extra], { window: windowNode, tenant }),
    [scope.nodes, scope.tenant, windowNode],
  );

  const onQuickFilter = useCallback(
    (namespace: string, value: string) => {
      if (namespace === 'tenant') {
        openInNewTab(gridHref([], Number(value)));
        return;
      }
      const cfg = namespaces[namespace];
      const operator = cfg ? equalityOperator(cfg.operators) : null;
      if (!operator) return;
      openInNewTab(gridHref([{ kind: 'cond', namespace, operator, value }]));
    },
    [namespaces, gridHref],
  );

  const open = useCallback(
    (row: ExternalAccessItem) => (onOpenRow ? onOpenRow(row) : setOpenRow(row)),
    [onOpenRow],
  );

  const columns = useMemo(() => {
    const keep = new Set(FIELDS[scope.kind]);
    return buildColumns({ showTenant: scope.kind !== 'tenant', onQuickFilter, onOpen: open }).filter((c) =>
      keep.has(c.field),
    );
  }, [scope.kind, onQuickFilter, open]);

  // Totals: an exact count when the server gave one; otherwise page on `has_next` and derive
  // the total on the last page (MUI will not — see lessons.md, `rowCount={-1}`).
  const data = query.data;
  const rowsOnPage = data?.results.length ?? 0;
  const hasNext = data
    ? (data.has_next ?? (data.count >= 0 ? (pagination.page + 1) * pagination.pageSize < data.count : false))
    : false;
  const rowCount = !data
    ? 0
    : data.count >= 0
      ? data.count
      : hasNext
        ? -1
        : pagination.page * pagination.pageSize + rowsOnPage;
  const paginationMeta = useMemo(() => ({ hasNextPage: hasNext }), [hasNext]);

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <TimeWindowPicker
          label="When"
          value={range}
          onChange={([from, to]) => {
            setRange(from && to ? [from, to] : defaultRange());
            setPagination((p) => ({ ...p, page: 0 }));
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          endIcon={<OpenInNewIcon fontSize="small" />}
          href={gridHref()}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in grid
        </Button>
      </Stack>

      {query.isError ? (
        <Alert severity="error">Couldn’t load the calls.</Alert>
      ) : data && data.results.length === 0 && pagination.page === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No external calls in this window.
        </Typography>
      ) : (
        <DataGridPro<ExternalAccessItem>
          rows={data?.results ?? []}
          columns={columns}
          getRowId={(r) => r.id}
          loading={query.isLoading || query.isFetching}
          density="compact"
          autoHeight
          pagination
          paginationMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          rowCount={rowCount}
          paginationMeta={paginationMeta}
          pageSizeOptions={[PAGE_SIZE]}
          disableColumnFilter
          disableColumnMenu
          disableColumnSorting
          disableRowSelectionOnClick
          onRowClick={(p) => open(p.row)}
          slotProps={{ loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' } }}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      )}

      {!onOpenRow && <ExternalAccessDetailDrawer row={openRow} onClose={() => setOpenRow(null)} />}
    </Box>
  );
}
